import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { fetchCurrentUser } from '@/lib/api/auth.ts';
import type { APIUser } from '@/types/index.ts';

/**
 * The currently authenticated user (`GET public/1/auth/myself/`). The `_logged`
 * route guard primes this via `ensureQueryData`, so any read inside a protected
 * route resolves straight from cache with no waterfall. `queryFn` returns `null`
 * when the session is gone.
 *
 * Polls every 60s (and refetches on window focus / reconnect once stale) so an
 * externally-changed identity — account edited, features toggled, or the session
 * ended / logged off elsewhere — surfaces on its own within ~a minute, with no
 * manual action. Polling pauses while the tab is hidden
 * (`refetchIntervalInBackground: false`). The 60s `staleTime` keeps the identity
 * stable across rapid navigation (cache-served, no waterfall — `ensureQueryData`
 * returns cached data even when stale) while still letting focus/reconnect
 * refresh it. Explicit identity transitions go through `setQueryData`/
 * `resetSession` (login / sudo / logout / 403 interceptor); when a poll resolves
 * `null` (session ended externally), the `_logged` route component redirects to
 * `/login`.
 */
export function currentUserQueryOptions() {
  return queryOptions({
    queryKey: ['auth', 'myself'],
    queryFn: fetchCurrentUser,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

/**
 * Read the authenticated user inside a protected (`_logged`) route. Guaranteed
 * non-null: the guard redirects unauthenticated visitors before any consumer
 * mounts, so the cache always holds a user here.
 */
export function useCurrentUser(): APIUser {
  const { data } = useSuspenseQuery(currentUserQueryOptions());
  if (!data) {
    throw new Error('useCurrentUser must be used within an authenticated (_logged) route');
  }
  return data;
}
